import { ReactNode, useEffect } from 'react'

import './Modal.global.scss'

export type ModalSize = 'short' | 'wide' | 'small'

const SIZE_CLASSES: Readonly<Record<ModalSize, { dialog: string; modal: string }>> = {
  short: { dialog: 'modal-dialog', modal: 'bootstrap-modal modal fade in hc-short-modal' },
  wide: { dialog: 'modal-dialog', modal: 'bootstrap-modal modal fade in hc-wide-modal' },
  small: { dialog: 'modal-dialog modal-sm', modal: 'modal fade in' },
}

/**
 * These modals are hand-rolled, so Bootstrap's plugin is never the one adding
 * `modal-open` to the body — without it the page behind scrolls instead.
 */
function _useScrollLock(open: boolean): void {
  useEffect(() => {
    if (!open) {
      return
    }
    document.body.classList.add('modal-open')
    return () => {
      document.body.classList.remove('modal-open')
    }
  }, [open])
}

export function Modal({
  open,
  onClose,
  title,
  size = 'short',
  id,
  showCloseButton = true,
  header,
  footer,
  children,
  dialogClassName = '',
}: {
  open: boolean
  onClose: () => void
  title?: ReactNode
  size?: ModalSize
  id?: string
  showCloseButton?: boolean
  header?: ReactNode
  footer?: ReactNode
  children: ReactNode
  dialogClassName?: string
}) {
  _useScrollLock(open)

  useEffect(() => {
    if (!open) {
      return
    }
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }
    document.addEventListener('keydown', closeOnEscape)
    return () => document.removeEventListener('keydown', closeOnEscape)
  }, [open, onClose])

  if (!open) {
    return null
  }

  const classes = SIZE_CLASSES[size]

  return (
    <>
      <div className={classes.modal} id={id} tabIndex={-1} role="dialog" aria-modal="true" style={{ display: 'block' }}>
        <div className={`${classes.dialog} ${dialogClassName}`.trim()} role="document">
          <div className="modal-content">
            {(header ?? title ?? showCloseButton) && (
              <div className="modal-header">
                {showCloseButton && (
                  <button type="button" className="close" aria-label="Cerrar" onClick={onClose}>
                    <span aria-hidden="true">×</span>
                  </button>
                )}
                {header ?? (title && <h4 className="modal-title">{title}</h4>)}
              </div>
            )}
            <div className="modal-body">{children}</div>
            {footer && <div className="modal-footer">{footer}</div>}
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade in" onClick={onClose} />
    </>
  )
}
